import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { Term } from "@/admin/types";
import {
  fetchTermByIdRemote,
  fetchTermsRemote,
  type TermsListPagination,
} from "@/lib/termsApi";
import {
  normalizeForCompare,
  textIncludesNormalized,
  wordFirstLetter,
} from "@/lib/normalizeText";
import {
  TERMS_PAGE_SIZES,
  TERMS_PAGE_SIZE_OPTIONS,
  type TermsPageSizeOption,
} from "@/lib/termsPageSizes";

export type { Term, TermsListPagination, TermsPageSizeOption };
export { TERMS_PAGE_SIZE_OPTIONS, TERMS_PAGE_SIZES };
export { wordFirstLetter } from "@/lib/normalizeText";

function compareTermsByWord(a: Term, b: Term): number {
  return normalizeForCompare(a.word).localeCompare(
    normalizeForCompare(b.word),
    undefined,
    { sensitivity: "base", numeric: true }
  );
}

/** Full catalog + client sort/filter (A–Z, letter, accent-insensitive search). */
function portalUsesFullCatalog(_options: UseTermsOptions): boolean {
  return true;
}

/** Portal visibility / featured rules applied after a paginated GET. */
export function filterPortalTerms(
  rows: Term[],
  options?: {
    status?: string;
    preferFeatured?: boolean;
    featuredOnly?: boolean;
  }
): Term[] {
  let list = [...rows];
  const status = (
    options?.status === "pending" || options?.status === "rejected"
      ? options.status
      : "approved"
  ) as Term["status"];
  list = list.filter(t => t.status === status);

  if (status === "approved") {
    list = list.filter(t => t.is_enabled);
  }

  if (options?.featuredOnly) {
    list = list.filter(t => t.featured);
  }

  if (options?.preferFeatured) {
    list.sort((a, b) => {
      const fa = a.featured ? 1 : 0;
      const fb = b.featured ? 1 : 0;
      if (fb !== fa) {
        return fb - fa;
      }
      return compareTermsByWord(a, b);
    });
  } else {
    list.sort(compareTermsByWord);
  }

  return list;
}

/** First character of `word` (API has no letter filter; diacritics ignored). */
export function termMatchesLetter(term: Term, letter: string): boolean {
  const L = letter.trim().toUpperCase().slice(0, 1);
  if (!L) {
    return true;
  }
  return wordFirstLetter(term.word) === L;
}

export function filterTermsByLetter(terms: Term[], letter?: string): Term[] {
  const L = letter?.trim().toUpperCase().slice(0, 1);
  if (!L) {
    return terms;
  }
  return terms.filter(t => termMatchesLetter(t, L));
}

/** Accent-insensitive match on word + meanings. Exact word match sorts first. */
export function filterTermsBySearch(terms: Term[], search?: string): Term[] {
  const q = normalizeForCompare(search ?? "");
  if (!q) {
    return terms;
  }
  const matched = terms.filter(
    t =>
      textIncludesNormalized(t.word, q) ||
      textIncludesNormalized(t.word_gujarati, q) ||
      textIncludesNormalized(t.word_devanagari, q) ||
      textIncludesNormalized(t.word_hindi, q) ||
      textIncludesNormalized(t.meaning_english, q) ||
      textIncludesNormalized(t.meaning_hindi, q) ||
      textIncludesNormalized(t.meaning_gujarati, q)
  );
  // Exact word match always comes first so the dictionary entry is immediately visible.
  return matched.sort((a, b) => {
    const aExact = normalizeForCompare(a.word) === q ? 0 : 1;
    const bExact = normalizeForCompare(b.word) === q ? 0 : 1;
    return aExact - bExact;
  });
}

export type UseTermsOptions = {
  page?: number;
  page_size?: number;
  letter?: string;
  search?: string;
  status?: string;
  preferFeatured?: boolean;
  featuredOnly?: boolean;
  limit?: number;
};

export type TermsPageResult = {
  terms: Term[];
  pagination: TermsListPagination | null;
};

function portalListRemoteOptions(
  options: UseTermsOptions
): Parameters<typeof fetchTermsRemote>[1] {
  return {
    search: options.search,
    portalAllStatuses:
      options.status === "pending" || options.status === "rejected",
    status:
      options.status === "pending" || options.status === "rejected"
        ? options.status === "rejected"
          ? "draft"
          : options.status
        : undefined,
    is_featured: options.featuredOnly ? true : undefined,
    fetchAllPages: options.featuredOnly ? true : undefined,
  };
}

/** Full approved portal catalog for client-side letter filter (API has no `letter` param). */
export async function fetchPortalTermsCatalog(
  options?: UseTermsOptions
): Promise<Term[]> {
  const { terms: rows, error } = await fetchTermsRemote("portal", {
    ...portalListRemoteOptions(options ?? {}),
    // Never forward `search` to the API — the catalog must be the complete dataset.
    // Hindi/Gujarati search terms would get no server results since the API only
    // indexes the Latin `word` field. All filtering is done client-side below.
    search: undefined,
    fetchAllPages: true,
    page_size: TERMS_PAGE_SIZES.fetchAllChunk,
  });

  if (error) {
    throw new Error(error);
  }

  return filterPortalTerms(rows ?? [], {
    status: options?.status,
    preferFeatured: options?.preferFeatured,
    featuredOnly: options?.featuredOnly,
  });
}

function paginateTermsClient(
  terms: Term[],
  page: number,
  page_size: number
): TermsPageResult {
  const totalCount = terms.length;
  const start = (page - 1) * page_size;
  return {
    terms: terms.slice(start, start + page_size),
    pagination: {
      count: totalCount,
      page,
      page_size,
    },
  };
}

async function fetchPortalTermsPage(
  options: UseTermsOptions,
  catalog?: Term[]
): Promise<TermsPageResult> {
  const page = Math.max(1, options.page ?? 1);
  const page_size = options.page_size ?? TERMS_PAGE_SIZES.browseDefault;
  const letter = options.letter?.trim().toUpperCase().slice(0, 1);

  if (portalUsesFullCatalog(options)) {
    const all = catalog ?? (await fetchPortalTermsCatalog(options));
    let terms = letter ? filterTermsByLetter(all, letter) : all;
    terms = filterTermsBySearch(terms, options.search);

    if (options.limit != null && options.limit > 0) {
      terms = terms.slice(0, options.limit);
      return { terms, pagination: { count: terms.length, page: 1, page_size } };
    }

    if (options.featuredOnly) {
      return {
        terms,
        pagination: {
          count: terms.length,
          page: 1,
          page_size: terms.length || page_size,
        },
      };
    }

    return paginateTermsClient(terms, page, page_size);
  }

  const { terms: rows, error, pagination } = await fetchTermsRemote("portal", {
    page,
    page_size,
    ...portalListRemoteOptions(options),
  });

  if (error) {
    throw new Error(error);
  }

  let terms = filterPortalTerms(rows ?? [], {
    status: options.status,
    preferFeatured: options.preferFeatured,
    featuredOnly: options.featuredOnly,
  });

  if (options.limit != null && options.limit > 0) {
    terms = terms.slice(0, options.limit);
  }

  return { terms, pagination };
}

export function useTerms(options?: UseTermsOptions) {
  const page = options?.page ?? 1;
  const page_size = options?.page_size ?? TERMS_PAGE_SIZES.browseDefault;
  const letter = options?.letter?.trim().toUpperCase().slice(0, 1) ?? "";
  const hasLetter = letter.length > 0;
  const useCatalog = portalUsesFullCatalog(options ?? {});

  const catalogQuery = useQuery({
    queryKey: [
      "terms",
      "portal-api",
      "catalog",
      options?.status ?? "approved",
      options?.preferFeatured ?? false,
      options?.featuredOnly ?? false,
    ],
    queryFn: () => fetchPortalTermsCatalog(options),
    enabled: useCatalog,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return useQuery({
    queryKey: [
      "terms",
      "portal-api",
      "page",
      page,
      page_size,
      letter,
      options?.search ?? "",
      options?.status ?? "approved",
      options?.preferFeatured ?? false,
      options?.featuredOnly ?? false,
      options?.limit ?? 0,
    ],
    queryFn: () =>
      fetchPortalTermsPage(
        { ...options, page, page_size },
        useCatalog ? catalogQuery.data : undefined
      ),
    enabled: useCatalog ? catalogQuery.isSuccess : true,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
    select: (result): Term[] => result.terms,
  });
}

/** Paginated portal list including API `count` / `page` / `page_size`. */
export function useTermsPage(
  options: UseTermsOptions & { page: number; enabled?: boolean }
) {
  const page_size = options.page_size ?? TERMS_PAGE_SIZES.browseDefault;
  const enabled = options.enabled !== false;
  const letter = options.letter?.trim().toUpperCase().slice(0, 1) ?? "";
  const useCatalog = portalUsesFullCatalog(options);

  const catalogQuery = useQuery({
    queryKey: [
      "terms",
      "portal-api",
      "catalog",
      options.status ?? "approved",
      options.preferFeatured ?? false,
      options.featuredOnly ?? false,
    ],
    queryFn: () => fetchPortalTermsCatalog(options),
    enabled: useCatalog && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  return useQuery({
    queryKey: [
      "terms",
      "portal-api",
      "page-meta",
      options.page,
      page_size,
      letter,
      options.search ?? "",
      options.status ?? "approved",
      options.preferFeatured ?? false,
      options.featuredOnly ?? false,
    ],
    queryFn: () =>
      fetchPortalTermsPage(
        { ...options, page: options.page, page_size },
        useCatalog ? catalogQuery.data : undefined
      ),
    enabled: useCatalog ? catalogQuery.isSuccess && enabled : enabled,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
    /** Keep prior page visible only while changing page/filters on the same screen — not across routes. */
    placeholderData: keepPreviousData,
  });
}

const EMPTY_TERMS: Term[] = [];

/** Autocomplete suggestions — server search, small page size. */
export function useTermSearchSuggestions(search: string, enabled = true) {
  const q = search.trim();
  return useQuery({
    queryKey: ["terms", "portal-api", "suggest", q],
    queryFn: async (): Promise<Term[]> => {
      const all = await fetchPortalTermsCatalog({});
      return filterTermsBySearch(all, q).slice(0, TERMS_PAGE_SIZES.autocomplete);
    },
    enabled: enabled && q.length >= 2,
    staleTime: 60 * 1000,
    select: (rows): Term[] => rows ?? EMPTY_TERMS,
  });
}

export function useTerm(id: string) {
  return useQuery({
    queryKey: ["term", "portal-api", id],
    queryFn: async (): Promise<Term> => {
      const { term, error } = await fetchTermByIdRemote(id, "portal");
      if (error) {
        throw new Error(error);
      }
      if (!term || (!term.id && !term.word)) {
        throw new Error("Term not found");
      }
      if (term.status !== "approved" || !term.is_enabled) {
        throw new Error("Term not found");
      }
      return term;
    },
    enabled: !!id.trim(),
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
}

export function useTermsCount() {
  return useQuery({
    queryKey: ["terms", "portal-api", "count"],
    queryFn: async (): Promise<number> => {
      const { pagination, error } = await fetchTermsRemote("portal", {
        page: 1,
        page_size: TERMS_PAGE_SIZES.countProbe,
      });
      if (error) {
        throw new Error(error);
      }
      return pagination?.count ?? 0;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
  });
}
